import dayjs from "dayjs";
import styles from "./index.module.css";
import axiosApi from "helpers/axios_config";
import paperPlaneTilt from "assets/svg/PaperPlaneTilt.svg";
import checkCircle from "assets/svg/CheckCircle.svg";
import money from "assets/svg/Money.svg";
import prohibit from "assets/svg/Prohibit.svg";
import handArrowUp from "assets/svg/HandArrowUp.svg";
import handArrowDown from "assets/svg/HandArrowDown.svg";
import handPalm from "assets/svg/HandPalm.svg";
import scales from "assets/svg/Scales.svg";
import gavel from "assets/svg/Gavel.svg";
import calendarDot from "assets/svg/CalendarDot.svg";

export type SystemMessageParams = {
  disputeId?: string | number;
  supportId?: string | number;
  supportIds?: (string | number)[];
  reportUrl?: string;
  date?: string;
  [key: string]: unknown;
};

type InfoField = "dispute" | "support" | "date";

type SystemMessageView = {
  icon: string;
  text: (params: SystemMessageParams) => string;
  /** Поля под текстом по макету админа. */
  info?: InfoField[];
};

const formatChangedDate = (date?: string) => (date ? dayjs(date).format("MMM D") : "");

export const SYSTEM_MESSAGES: Record<string, SystemMessageView> = {
  RENTAL_REQUESTED: { icon: paperPlaneTilt, text: () => "Rental requested" },
  RENTAL_ACCEPTED: { icon: checkCircle, text: () => "Rental accepted" },
  RENTAL_PAID: { icon: money, text: () => "Rental paid" },
  RENTAL_CANCELLED: { icon: prohibit, text: () => "Rental cancelled" },
  RENTAL_COMPLETED: { icon: checkCircle, text: () => "Rental completed" },
  PRODUCT_TRANSFERRED: { icon: handArrowUp, text: () => "Product transferred" },
  PRODUCT_RETURNED: { icon: handArrowDown, text: () => "Product returned" },
  RETURN_DATE_CHANGED: { icon: calendarDot, text: ({ date }) => `Return date changed to ${formatChangedDate(date)}` },
  TRANSFER_DATE_CHANGED: {
    icon: calendarDot,
    text: ({ date }) => `Transfer date changed to ${formatChangedDate(date)}`,
  },
  DISPUTE_INITIATED: { icon: handPalm, text: () => "The dispute has been requested", info: ["dispute"] },
  DISPUTE_DECLINED: { icon: prohibit, text: () => "The dispute has been declined", info: ["dispute", "support"] },
  DISPUTE_CONFIRMED: {
    icon: scales,
    text: () => "The dispute has been confirmed",
    info: ["dispute", "support", "date"],
  },
  DISPUTE_COMPLETED: {
    icon: gavel,
    text: () => "The dispute has been completed",
    info: ["dispute", "support", "date"],
  },
};

type Props = {
  messageId: number;
  messageKey: string;
  createdAt: string;
  params?: SystemMessageParams;
};

const shortId = (id: string | number) => String(id).slice(0, 7);

export const SystemMessage = ({ messageId, messageKey, createdAt, params = {} }: Props) => {
  const { icon, text, info = [] } = SYSTEM_MESSAGES[messageKey];
  // Время события приходит в параметре *At (requestedAt, declinedAt, completedAt и т.д.).
  const eventTime = Object.entries(params).find(([key, v]) => key.endsWith("At") && typeof v === "string")?.[1] as
    | string
    | undefined;
  const supportIds = [...new Set([...(params.supportIds ?? []), params.supportId].filter(Boolean).map(String))];
  const reportUrl = params.reportUrl ? new URL(params.reportUrl, axiosApi.defaults.baseURL).href : null;

  return (
    <div className={styles.systemEvent} id={`message-${messageId}`}>
      <div className={styles.systemEventHeader}>
        <div className={styles.systemEventTitle}>
          <img src={icon} alt={messageKey} />
          <span>{text(params)}</span>
        </div>
        {reportUrl ? (
          <a className={styles.systemEventId} href={reportUrl} target="_blank" rel="noreferrer">
            Dispute Report
          </a>
        ) : null}
      </div>
      {info.length ? (
        <div className={styles.systemEventInfo}>
          {info.includes("dispute") && params.disputeId ? (
            <span>
              Dispute ID <span className={styles.systemEventId}>{shortId(params.disputeId)}</span>
            </span>
          ) : null}
          {info.includes("support") && supportIds.length ? (
            <span>
              Support ID <span className={styles.systemEventId}>{supportIds.map(shortId).join(" – ")}</span>
            </span>
          ) : null}
          {info.includes("date") ? (
            <span>
              Date <span>{dayjs(eventTime ?? createdAt).format("MMM, D")}</span>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
